import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import {
  ANIMATION_OPTIONS, VERTICAL_ANIMATION_SLIDE, HORIZONTAL_ANIMATION_SLIDE, getAnimationSyncTime,
} from '@routific/hive';
import { IColor } from '../../types/IColor';
import getBackgroundColor from '../../helpers/getBackgroundColors';

interface Props {
  color?: IColor;
  width?: number;
  height?: number;
  style?: React.CSSProperties;
  loading?: boolean;
  horizontal?: boolean;
  disabled?: boolean;
}

const Bar = styled.span<{ width?: number, height?: number, horizontal?: boolean }>`
  display: inline-flex;
  border-radius: 0 4px 4px 0;
  width: ${({ width }) => `${width || 4}px`};
  height: ${({ height, horizontal }) => {
    if (horizontal) {
      return '2px';
    }
    return height ? `${height}px` : 'auto';
  }};
  min-width: 4px;
  min-height: ${({ horizontal }) => `${horizontal ? 2 : 8}px`};;
  position: relative;
  overflow: hidden;

`;

const InnerBar = styled.span`
  position:absolute;
  border-radius:10px;
  top:0;
  right:0;
  bottom:100%;
  left:0;
  background: currentColor; 
  min-width:100%;
`;

const ColorBar: React.FC<Props> = ({
  color, width, height, style, loading, horizontal, disabled,
}) => {

  const backgroundColor = getBackgroundColor(!!loading, !!disabled, color);
  
  const itemRef = useRef() as React.RefObject<HTMLSpanElement>;

  useEffect(() => {
    let animation: Animation | undefined;
    if (document?.timeline?.currentTime && itemRef.current) {
      animation = itemRef.current?.animate(horizontal ? HORIZONTAL_ANIMATION_SLIDE : VERTICAL_ANIMATION_SLIDE, ANIMATION_OPTIONS as KeyframeAnimationOptions);

      if (animation) {
        animation.cancel();

        if (loading) {
          animation.startTime = getAnimationSyncTime();
          animation.play();
        } else {
          animation.cancel();
        }
      }
    }
    // Clean up animation when unmounting
    return () => { animation?.cancel(); };
  }, [loading, itemRef]);

  const styleProps = {
    width, height, horizontal, style: { ...style, background: backgroundColor, color: color?.hex },
  };

  return (
    <Bar
      {...styleProps}
      data-cy="color-bar"
      data-testid="color-bar"
    >
      {loading
        && <InnerBar ref={itemRef} data-cy="loading-indicator" />}
    </Bar>
  );
};

export default ColorBar;
